/* SneakBoutik : comportement commun (cartes, favoris, panier Shopify, avis, compte à rebours).
   Les données produits viennent de Shopify (#sb-products, #sb-catalog, #sb-product). */
(() => {
'use strict';

const SB = window.SB;
const S = SB.settings;
const readJson = (id) => {
  const el = document.getElementById(id);
  try { return el ? JSON.parse(el.textContent) : null; } catch { return null; }
};

// ---------- Marque et nom du modèle ----------
// Le titre Shopify contient la marque (« Adidas Gazelle… ») ; le fournisseur est celui de la boutique.
// Si le fournisseur est renseigné avec la vraie marque, il est utilisé en priorité.
const KNOWN_BRANDS = ['NEW BALANCE', 'NIKE', 'ADIDAS', 'PUMA', 'ASICS', 'REEBOK', 'VANS', 'CONVERSE', 'JORDAN', 'SAUCONY', 'SALOMON', 'FILA', 'LE COQ SPORTIF'];
const MODEL_BRANDS = { 'STAN SMITH': 'ADIDAS', SUPERSTAR: 'ADIDAS', GAZELLE: 'ADIDAS', SAMBA: 'ADIDAS', 'AIR MAX': 'NIKE', 'AIR FORCE': 'NIKE' };
function normalize(p) {
  const title = p.title.trim();
  const upper = title.toUpperCase();
  const vendor = (p.vendor || '').toUpperCase();
  let brand = KNOWN_BRANDS.find((b) => upper.startsWith(`${b} `) || upper === b);
  let name = brand ? title.slice(brand.length).trim() : title;
  if (!brand) brand = KNOWN_BRANDS.includes(vendor) ? vendor : Object.entries(MODEL_BRANDS).find(([m]) => upper.startsWith(m))?.[1] ?? vendor;
  name = name.charAt(0).toUpperCase() + name.slice(1);
  return { ...p, brand, name, compareAt: p.compareAt ?? null };
}

const ALL = (readJson('sb-products') ?? []).map(normalize);
const BY_HANDLE = new Map(ALL.map((p) => [p.handle, p]));

// ---------- Formats ----------
const LOCALE = 'fr-FR'; // boutique destinée à la France : dates et nombres toujours au format français
const fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: SB.currency || 'EUR' });
const FREE_SHIPPING_FROM = S.freeShippingFrom ?? null;
const hasFreeShipping = (p) => FREE_SHIPPING_FROM != null && p.price >= FREE_SHIPPING_FROM;
// Fin d'offre : date précise, ou chaque semaine (jour et heure de Paris), ou aucune.
function parisOffsetMs(date) {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Paris', timeZoneName: 'shortOffset' })
    .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+1';
  const [, sign, h, m] = name.match(/GMT([+-])(\d+)(?::(\d+))?/) ?? [0, '+', '1', '0'];
  return (sign === '-' ? -1 : 1) * (Number(h) * 60 + Number(m || 0)) * 60000;
}
function nextWeeklyEnd(now = new Date()) {
  const paris = new Date(now.getTime() + parisOffsetMs(now)); // heure de Paris lue en UTC
  const target = new Date(paris);
  target.setUTCHours(S.weeklyHour, 59, 59, 0);
  let days = (S.weeklyDay - paris.getUTCDay() + 7) % 7;
  if (days === 0 && target <= paris) days = 7;
  target.setUTCDate(target.getUTCDate() + days);
  return new Date(target.getTime() - parisOffsetMs(target));
}
let PROMO_END = null;
if (S.countdownMode === 'date' && S.promoEnd) PROMO_END = new Date(S.promoEnd);
if (S.countdownMode === 'weekly') PROMO_END = nextWeeklyEnd();

// Shopify renvoie « Default Title » quand il n'y a pas de pointure ; « 38.5 » -> « 38,5 »
const sizeLabel = (s) => (s === 'Default Title' ? '' : String(s).replace('.', ','));
const sizeValue = (s) => {
  const [n, frac] = String(s).split(' ');
  if (!frac) return parseFloat(n);
  const [a, b] = frac.split('/');
  return parseFloat(n) + a / b;
};
const ALL_SIZES = [...new Set(ALL.flatMap((p) => p.sizes))]
  .filter((s) => s !== 'Default Title' && !Number.isNaN(sizeValue(s)))
  .sort((a, b) => sizeValue(a) - sizeValue(b));

const isPromo = (p) => p.compareAt > p.price;
const discount = (p) => Math.round((1 - p.price / p.compareAt) * 100);
const imgUrl = (src, width = 640) => {
  try { const u = new URL(src, location.href); u.searchParams.set('width', width); return u.href; } catch { return src; }
};
const productUrl = (p) => p.url;
const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const titleCase = (s) => s.charAt(0) + s.slice(1).toLowerCase();

function stockInfo(p) {
  // stock = nombre de variantes disponibles (une paire chacune) ; sizes = pointures disponibles
  const count = p.stock ?? p.sizes.length;
  const sizes = p.sizes.map(sizeLabel).filter(Boolean);
  if (!count) return { text: 'Épuisé', cls: 'is-out' };
  if (count === 1) return { text: `Dernière paire${sizes[0] ? ` · ${sizes[0]}` : ''}`, cls: 'is-last' };
  return { text: `Plus que ${count} paires · ${sizes.join(' / ')}`, cls: 'is-low' };
}

// ---------- Préférences locales (favoris, pointure mémorisée) ----------
const store = {
  get: (k, d) => { try { return JSON.parse(localStorage.getItem(`sb:${k}`) ?? JSON.stringify(d)); } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(`sb:${k}`, JSON.stringify(v)); } catch { /* stockage indisponible */ } },
};

// ---------- Panier Shopify (API Ajax) ----------
const cart = {
  variants: new Set(SB.cartVariants),
  count: SB.cartCount,
  async request(path, body) {
    const res = await fetch(`${SB.root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Le panier n’a pas pu être mis à jour.');
    return data;
  },
  async refresh() {
    const data = await this.request('cart.js');
    this.apply(data);
    return data;
  },
  apply(data) {
    this.items = data.items;
    this.count = data.item_count;
    this.variants = new Set(data.items.map((i) => i.variant_id));
    refreshHeader();
    document.dispatchEvent(new CustomEvent('sb:cartchange'));
  },
  async add(variantId) {
    await this.request('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
    return this.refresh();
  },
  async remove(key) {
    const data = await this.request('cart/change.js', { id: key, quantity: 0 });
    this.apply(data);
    return data;
  },
};

function refreshHeader() {
  const wish = store.get('wish', []);
  const w = document.getElementById('wish-count');
  const c = document.getElementById('cart-count');
  if (w) { w.textContent = wish.length; w.hidden = !wish.length; }
  if (c) { c.textContent = cart.count; c.hidden = !cart.count; }
}

const isWished = (handle) => store.get('wish', []).includes(handle);
function toggleWish(handle) {
  const wish = new Set(store.get('wish', []));
  wish.has(handle) ? wish.delete(handle) : wish.add(handle);
  store.set('wish', [...wish]);
  refreshHeader();
  syncWishButtons();
  return wish.has(handle);
}
// Tous les boutons cœur portent data-wish="handle" : un seul endroit pour les mettre à jour
function syncWishButtons() {
  document.querySelectorAll('[data-wish]').forEach((b) => {
    const on = isWished(b.dataset.wish);
    b.classList.toggle('is-active', on);
    b.setAttribute('aria-pressed', on);
  });
}

// ---------- Carte produit ----------
const HEART = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 7.9 3.6 4.5 7.1 4.5c2.1 0 3.6 1.2 4.9 2.9 1.3-1.7 2.8-2.9 4.9-2.9 3.5 0 5.7 3.4 4.4 6.8-1.8 4.6-9.3 9.2-9.3 9.2z"/></svg>';

function renderCard(p) {
  const li = document.createElement('li');
  li.className = `card${p.sizes.length ? '' : ' is-soldout'}`;
  const stock = stockInfo(p);
  const name = escapeHtml(p.name);
  li.innerHTML = `
    <a class="card__link" href="${productUrl(p)}">
      <div class="card__media sk">
        ${p.images[0] ? `<img class="card__img" loading="lazy" src="${imgUrl(p.images[0])}" alt="${escapeHtml(p.brand)} ${name}">` : ''}
        ${p.images[1] ? `<img class="card__img card__img--alt" loading="lazy" src="${imgUrl(p.images[1])}" alt="">` : ''}
        <button class="card__wish${isWished(p.handle) ? ' is-active' : ''}" data-wish="${p.handle}" aria-pressed="${isWished(p.handle)}" aria-label="Ajouter aux favoris" type="button">${HEART}</button>
        <div class="card__badges">
          ${isPromo(p) ? `<span class="badge badge--promo">-${discount(p)}%</span>` : ''}
        </div>
      </div>
      <div class="card__info">
        <p class="card__brand">${escapeHtml(p.brand)}</p>
        <p class="card__name">${name}</p>
        <p class="card__stock ${stock.cls}">${stock.text}</p>
        ${FREE_SHIPPING_FROM != null ? `<p class="card__shipping${hasFreeShipping(p) ? '' : ' is-hidden'}">Livraison gratuite</p>` : ''}
        <p class="card__price">
          <span class="card__now${isPromo(p) ? ' is-promo' : ''}">${fmt.format(p.price)}</span>
          ${isPromo(p) ? `<s class="card__was">${fmt.format(p.compareAt)}</s>` : ''}
        </p>
      </div>
    </a>`;
  li.querySelector('.card__wish').addEventListener('click', (e) => {
    e.preventDefault();
    toggleWish(p.handle);
  });
  const img = li.querySelector('.card__img');
  if (img) revealOnLoad(img, li.querySelector('.card__media'));
  else li.querySelector('.card__media').classList.remove('sk');
  return li;
}

// Le conteneur garde son reflet animé jusqu'à ce que la photo soit chargée
function revealOnLoad(img, box) {
  const done = () => box.classList.remove('sk');
  if (img.complete && img.naturalWidth) done();
  else {
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  }
}

// ---------- Compte à rebours (uniquement si une vraie date de fin est réglée) ----------
const pad = (n) => String(n).padStart(2, '0');
function tickCountdowns() {
  const left = PROMO_END && !Number.isNaN(PROMO_END.getTime()) ? Math.max(0, PROMO_END - Date.now()) : 0;
  document.querySelectorAll('[data-countdown-wrap]').forEach((el) => { el.hidden = !left; });
  if (!left && S.countdownMode === 'weekly') { PROMO_END = nextWeeklyEnd(); return tickCountdowns(); }
  if (!left) return clearInterval(tickCountdowns.timer);
  const s = Math.floor(left / 1000);
  const [d, h, m, sec] = [Math.floor(s / 86400), Math.floor(s / 3600) % 24, Math.floor(s / 60) % 60, s % 60];
  document.querySelectorAll('[data-countdown="boxes"]').forEach((el) => {
    el.innerHTML = [[d, 'jours'], [h, 'h'], [m, 'min'], [sec, 's']]
      .map(([v, u]) => `<span class="cd-box"><b>${pad(v)}</b><small>${u}</small></span>`).join('');
  });
}
tickCountdowns();
tickCountdowns.timer = setInterval(tickCountdowns, 1000);

// ---------- Notification ----------
function toast(msg, action) {
  let t = document.getElementById('toast');
  if (!t) {
    t = Object.assign(document.createElement('div'), { id: 'toast', className: 'toast' });
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    document.body.append(t);
  }
  t.replaceChildren(Object.assign(document.createElement('span'), { textContent: msg }));
  if (action) {
    const b = Object.assign(document.createElement('button'), { type: 'button', className: 'toast__action', textContent: action.label });
    b.addEventListener('click', () => { t.classList.remove('is-visible'); action.run(); });
    t.append(b);
  }
  t.classList.add('is-visible');
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => t.classList.remove('is-visible'), action ? 4000 : 2600);
}

// ---------- Panier et favoris : panneau latéral ----------
const CLOSE_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 5l14 14M19 5 5 19"/></svg>';
const PAYMENTS_HTML = document.getElementById('sb-payments')?.innerHTML ?? '';

document.body.insertAdjacentHTML('beforeend', `
  <div class="panel-overlay" id="panel-overlay" hidden></div>
  <aside class="panel" id="panel" role="dialog" aria-modal="true" aria-labelledby="panel-title" hidden>
    <div class="panel__head">
      <p class="panel__title" id="panel-title"></p>
      <button class="close-btn" id="panel-close" type="button" aria-label="Fermer">${CLOSE_ICON}</button>
    </div>
    <div class="panel__body" id="panel-body"></div>
    <div class="panel__foot" id="panel-foot"></div>
  </aside>`);

let panelMode = null;
let panelOpener = null;

function lineHtml(p, meta, actions) {
  const name = escapeHtml(p.name);
  return `
    <li class="line">
      <a class="line__media" href="${p.url}">${p.image ? `<img src="${imgUrl(p.image, 200)}" alt="" loading="lazy">` : ''}</a>
      <div class="line__info">
        <p class="line__brand">${escapeHtml(p.brand)}</p>
        <a class="line__name" href="${p.url}">${name}</a>
        <p class="line__meta">${meta}</p>
        <p class="line__price">
          <span class="${isPromo(p) ? 'is-promo' : ''}">${fmt.format(p.price)}</span>
          ${isPromo(p) ? `<s>${fmt.format(p.compareAt)}</s>` : ''}
        </p>
      </div>
      ${actions}
    </li>`;
}

// Ligne du panier Shopify -> données d'affichage (prix et marque repris du catalogue si possible)
function cartLine(item) {
  const known = BY_HANDLE.get(item.handle);
  const base = known ?? normalize({ title: item.product_title, vendor: item.vendor, price: 0, sizes: [], images: [] });
  return {
    brand: base.brand,
    name: base.name,
    url: item.url,
    image: item.image,
    price: item.final_price / 100,
    compareAt: known?.compareAt ?? (item.original_price > item.final_price ? item.original_price / 100 : null),
    size: item.variant_title,
    options: (item.options_with_values ?? []).filter((o) => o.value !== 'Default Title'),
    key: item.key,
  };
}

async function renderPanel() {
  if (!panelMode) return;
  const body = document.getElementById('panel-body');
  const foot = document.getElementById('panel-foot');
  const empty = (title, text) => {
    body.innerHTML = `
      <div class="panel__empty">
        <p class="panel__empty-title">${title}</p>
        <p>${text}</p>
        <a class="btn btn--dark" href="${SB.root}#catalogue">Voir les baskets</a>
      </div>`;
    foot.hidden = true;
  };

  if (panelMode === 'cart') {
    document.getElementById('panel-title').textContent = 'Panier';
    let data;
    try { data = await cart.refresh(); } catch (err) {
      body.innerHTML = `<div class="panel__empty"><p>${escapeHtml(err.message)}</p></div>`;
      foot.hidden = true;
      return;
    }
    if (panelMode !== 'cart') return;
    const lines = data.items.map(cartLine);
    document.getElementById('panel-title').textContent = `Panier${lines.length ? ` (${lines.length})` : ''}`;
    if (!lines.length) return empty('Ton panier est vide', 'Les paires partent vite : ajoute celle qui te plaît avant qu\'un autre ne le fasse.');
    body.innerHTML = `<ul class="lines">${lines.map((l) => lineHtml(
      l,
      l.options.length ? l.options.map((o) => (/pointure|taille|size/i.test(o.name) || !Number.isNaN(sizeValue(o.value))
        ? `Pointure ${escapeHtml(sizeLabel(o.value))}` : `${escapeHtml(o.name)} : ${escapeHtml(o.value)}`)).join(' · ') : 'Taille unique',
      `<button class="line__remove" type="button" data-remove="${l.key}" aria-label="Retirer du panier">${CLOSE_ICON}</button>`,
    )).join('')}</ul>`;
    const total = data.total_price / 100;
    const saved = lines.reduce((sum, l) => sum + (isPromo(l) ? l.compareAt - l.price : 0), 0);
    foot.hidden = false;
    foot.innerHTML = `
      <div class="panel__total"><span>Sous-total</span><b>${fmt.format(total)}</b></div>
      ${saved ? `<p class="panel__saved">Tu économises ${fmt.format(saved)}</p>` : ''}
      <p class="panel__note">Frais d'expédition calculés à l'étape de paiement. Les paires ne sont réservées qu'une fois la commande validée.</p>
      <form action="${SB.cartUrl}" method="post"><button class="btn btn--orange btn--lg panel__cta" type="submit" name="checkout">Commander</button></form>
      ${PAYMENTS_HTML ? `<ul class="payments payments--panel" aria-label="Moyens de paiement">${PAYMENTS_HTML}</ul>` : ''}`;
    body.querySelectorAll('[data-remove]').forEach((b) => b.addEventListener('click', async () => {
      b.disabled = true;
      try { await cart.remove(b.dataset.remove); } catch (err) { toast(err.message); }
      renderPanel();
    }));
    return;
  }

  const items = store.get('wish', []).map((h) => BY_HANDLE.get(h)).filter(Boolean);
  document.getElementById('panel-title').textContent = `Favoris${items.length ? ` (${items.length})` : ''}`;
  if (!items.length) return empty('Aucun favori pour l\'instant', 'Touche le cœur d\'une paire pour la retrouver ici.');
  body.innerHTML = `<ul class="lines">${items.map((p) => {
    const stock = stockInfo(p);
    return lineHtml(
      { ...p, image: p.images[0] },
      `<span class="line__stock ${stock.cls}">${stock.text}</span>`,
      `<button class="line__remove" type="button" data-unwish="${p.handle}" aria-label="Retirer des favoris">${CLOSE_ICON}</button>`,
    );
  }).join('')}</ul>`;
  foot.hidden = true;
  body.querySelectorAll('[data-unwish]').forEach((b) => b.addEventListener('click', () => {
    toggleWish(b.dataset.unwish);
    renderPanel();
  }));
}

function openPanel(mode) {
  panelMode = mode;
  panelOpener = document.activeElement;
  renderPanel();
  document.getElementById('panel-overlay').hidden = false;
  document.getElementById('panel').hidden = false;
  document.body.classList.add('sheet-open');
  document.getElementById('panel-close').focus({ preventScroll: true });
}
function closePanel() {
  if (!panelMode) return;
  panelMode = null;
  document.getElementById('panel-overlay').hidden = true;
  document.getElementById('panel').hidden = true;
  document.body.classList.remove('sheet-open');
  panelOpener?.focus({ preventScroll: true });
}
document.getElementById('cart-open')?.addEventListener('click', (e) => { e.preventDefault(); openPanel('cart'); });
document.getElementById('wish-open')?.addEventListener('click', () => openPanel('wish'));
document.getElementById('panel-close').addEventListener('click', closePanel);
document.getElementById('panel-overlay').addEventListener('click', closePanel);
document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePanel(); });

// ---------- Recherche sur mobile : la loupe ouvre le champ ----------
document.getElementById('search-toggle')?.addEventListener('click', (e) => {
  const header = document.querySelector('.header');
  const open = header.classList.toggle('is-searching');
  e.currentTarget.setAttribute('aria-expanded', open);
  if (open) header.querySelector('.search input').focus();
});

// ---------- Avis clients ----------
// Avis d'exemple (réglage « Afficher les avis d'exemple ») : fictifs et marqués « Exemple ».
// En production, les avis Judge.me s'affichent via les blocs d'application de la section.
const REVIEWS = S.reviewsDemo ? window.REVIEWS : null;
const ratingFmt = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const dateFmt = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
const DEMO_TAG = '<span class="demo-tag" title="Avis fictifs pour la démonstration">Exemple</span>';

function starsHtml(rating, label = true) {
  const pct = (rating / 5) * 100;
  return `<span class="stars" ${label ? `role="img" aria-label="${ratingFmt.format(rating)} sur 5"` : 'aria-hidden="true"'}>
    <span class="stars__base">★★★★★</span><span class="stars__fill" style="width:${pct}%">★★★★★</span>
  </span>`;
}

function reviewHtml(r) {
  return `
    <li class="review">
      <div class="review__top">
        ${starsHtml(r.rating)}
        <time class="review__date" datetime="${r.createdAt}">${dateFmt.format(new Date(r.createdAt))}</time>
      </div>
      <p class="review__body">${escapeHtml(r.body)}</p>
      <div class="review__meta">
        <span class="review__author">${escapeHtml(r.author)}</span>
        <span class="review__verified">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>
          Achat vérifié
        </span>
        <span class="review__product">${escapeHtml(r.product)} · acheté le ${dateFmt.format(new Date(r.orderedAt))}</span>
      </div>
      <button class="review__report" type="button">Signaler</button>
    </li>`;
}

if (REVIEWS) {
  document.querySelectorAll('[data-rating]').forEach((el) => {
    el.innerHTML = `${starsHtml(REVIEWS.rating, false)}<b>${ratingFmt.format(REVIEWS.rating)}/5</b><span>${REVIEWS.count} avis${REVIEWS.demo ? ' · exemple' : ''}</span>`;
    el.setAttribute('aria-label', `Note moyenne ${ratingFmt.format(REVIEWS.rating)} sur 5, ${REVIEWS.count} avis. Voir les avis`);
    el.hidden = false;
  });

  document.querySelectorAll('[data-reviews]').forEach((section) => {
    const limit = Number(section.dataset.reviews);
    const bars = [5, 4, 3, 2, 1].map((n) => {
      const c = REVIEWS.distribution[n] ?? 0;
      return `<li class="dist__row"><span>${n} ★</span><span class="dist__bar"><span style="width:${(c / REVIEWS.count) * 100}%"></span></span><span class="dist__count">${c}</span></li>`;
    }).join('');
    section.innerHTML = `
      <div class="reviews__head">
        <h2 class="reviews__title" id="avis-title">Avis clients</h2>
        ${REVIEWS.demo ? DEMO_TAG : ''}
      </div>
      <div class="reviews__layout">
        <div class="reviews__summary">
          <p class="reviews__score">${ratingFmt.format(REVIEWS.rating)}<span>/5</span></p>
          ${starsHtml(REVIEWS.rating)}
          <p class="reviews__count">${REVIEWS.count} avis sur la boutique</p>
          <ul class="dist">${bars}</ul>
          <p class="reviews__policy">Chaque avis est demandé par e-mail après une commande et rattaché à cet achat. Les avis sont publiés sans modification, quelle que soit leur note.</p>
        </div>
        <div class="reviews__main">
          <div class="reviews__toolbar">
            <label class="reviews__sort">Trier par
              <select data-sort>
                <option value="recent">Plus récents</option>
                <option value="best">Mieux notés</option>
                <option value="worst">Moins bien notés</option>
              </select>
            </label>
          </div>
          <ul class="reviews__list"></ul>
          <button class="btn btn--ghost reviews__more" type="button">Voir tous les avis</button>
        </div>
      </div>`;
    const list = section.querySelector('.reviews__list');
    const SORT = {
      recent: (a, b) => b.createdAt.localeCompare(a.createdAt),
      best: (a, b) => b.rating - a.rating || b.createdAt.localeCompare(a.createdAt),
      worst: (a, b) => a.rating - b.rating || b.createdAt.localeCompare(a.createdAt),
    };
    let shown = limit;
    const draw = () => {
      const sorted = [...REVIEWS.items].sort(SORT[section.querySelector('[data-sort]').value]);
      list.innerHTML = sorted.slice(0, shown).map(reviewHtml).join('');
      section.querySelector('.reviews__more').hidden = shown >= sorted.length;
      list.querySelectorAll('.review__report').forEach((b) => b.addEventListener('click', () => {
        b.textContent = 'Signalé';
        b.disabled = true;
        toast('Merci, l’avis sera examiné');
      }));
    };
    section.querySelector('[data-sort]').addEventListener('change', draw);
    section.querySelector('.reviews__more').addEventListener('click', () => { shown = REVIEWS.items.length; draw(); });
    draw();
  });
}

// Recherche de l'en-tête : hors catalogue, envoie vers l'accueil filtré
document.querySelector('.search')?.addEventListener('submit', (e) => {
  if (document.getElementById('grid')) e.preventDefault();
});

refreshHeader();
syncWishButtons();
// Cœurs des cartes rendues par le serveur (les cartes du script ont leur propre écouteur)
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-ssr] .card__wish');
  if (!b) return;
  e.preventDefault();
  toggleWish(b.dataset.wish);
});

// ---------- Apparition des sections au défilement ----------
if (!matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
  document.documentElement.classList.add('js-reveal');
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (!e.isIntersecting) return;
    e.target.classList.add('is-in');
    io.unobserve(e.target);
  }), { rootMargin: '0px 0px -8% 0px' });
  document.querySelectorAll('.sb-block, .sb-banner, .reviews, .related__title, .assurance, .footer__main, .delivery, .reassure, .collection-bar')
    .forEach((el) => { el.classList.add('sb-reveal'); io.observe(el); });
}

// Outils partagés avec les scripts du catalogue et de la fiche produit
window.SBX = {
  ALL, BY_HANDLE, ALL_SIZES, normalize, fmt, sizeLabel, sizeValue, isPromo, discount, imgUrl, escapeHtml, titleCase,
  stockInfo, store, cart, refreshHeader, toggleWish, syncWishButtons, isWished, renderCard, revealOnLoad, toast, openPanel,
  hasFreeShipping, readJson,
};
window.SB_READY = true;
document.dispatchEvent(new CustomEvent('sb:ready'));
})();
